import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowDownLeft, Check, X, Lightbulb } from "lucide-react";

// ─────────────────────────────────────────────────────────────────────
// Tableau récapitulatif : sens des variations par type de compte
// ─────────────────────────────────────────────────────────────────────
const VARIATIONS_ROWS = [
  {
    type: "Comptes de Bilan",
    cells: [
      { label: "Comptes d'Actifs", growSide: "left", examples: "211, 411, 512" },
      { label: "Comptes de Passifs", growSide: "right", examples: "164, 401, 44571" },
      { label: "Comptes de Capitaux Propres", growSide: "right", examples: "101, 106, 120" },
    ],
  },
  {
    type: "Comptes de Gestion",
    cells: [
      { label: "Comptes de Charges", growSide: "left", examples: "601, 613, 6411" },
      { label: "Comptes de Produits", growSide: "right", examples: "701, 706, 707" },
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────
// Vérifiez-vous : 6 questions tirées des notions clés
// ─────────────────────────────────────────────────────────────────────
const QUIZZES = [
  {
    q: "Sur un compte en T, la colonne crédit se situe…",
    options: ["À gauche", "À droite", "En bas"],
    good: 1,
    explain:
      "Convention universelle : débit à gauche, crédit à droite. C'est purement conventionnel mais immuable.",
  },
  {
    q: "L'entreprise emprunte 50 000 € à la banque. L'emprunt (compte 164) est…",
    options: [
      "Un actif, qui augmente au débit",
      "Un passif, qui augmente au crédit",
      "Une charge",
    ],
    good: 1,
    explain:
      "Un emprunt est une dette → passif. Sa hausse se traduit par un crédit en 164. En contrepartie, la trésorerie (actif) augmente en 512 au débit.",
  },
  {
    q: "Vous payez un loyer de 800 € (compte 613). Ce loyer va…",
    options: [
      "Au débit (charge → augmente côté gauche)",
      "Au crédit (c'est une sortie)",
      "Au débit (mais en classe 4)",
    ],
    good: 0,
    explain:
      "Le loyer est une charge (classe 6) → débit du 613 pour 800 €. La contrepartie est en classe 5 (512 Banque crédit 800 €).",
  },
  {
    q: "Vous voyez une écriture déséquilibrée : Débit 3 500, Crédit 3 600. Que faire ?",
    options: [
      "L'enregistrer quand même",
      "Refuser et chercher l'erreur (oubli de ligne, faute de frappe…)",
      "Arrondir au plus proche",
    ],
    good: 1,
    explain:
      "Une écriture déséquilibrée est INTERDITE. Les logiciels comptables bloquent l'enregistrement. Il faut systématiquement trouver l'écart.",
  },
  {
    q: "Un compte 411 (Clients) est en classe 4. Son sens normal est…",
    options: [
      "Toujours débiteur",
      "Toujours créditeur",
      "Débiteur dans 99 % des cas (le client doit de l'argent)",
    ],
    good: 2,
    explain:
      "Le 411 est un compte de tiers (classe 4). Par défaut, un client nous DOIT de l'argent → débit. Si exceptionnellement on lui doit (avoir, trop-perçu), le solde devient créditeur ou on bascule en 419.",
  },
  {
    q: "Une augmentation de capital social (compte 101) s'enregistre…",
    options: [
      "Au débit (101 est un actif)",
      "Au crédit (101 est un capital propre, qui augmente à droite)",
      "Indifférent",
    ],
    good: 1,
    explain:
      "Le 101 Capital fait partie des capitaux propres. Comme les passifs, il augmente au CRÉDIT. La contrepartie (versement des associés) tombe au débit du 512 Banque.",
  },
];

// ─────────────────────────────────────────────────────────────────────
// Composant principal
// ─────────────────────────────────────────────────────────────────────
export default function Methodologie() {
  const [answers, setAnswers] = useState({});

  const pick = (idx, opt) => {
    setAnswers((a) => ({ ...a, [idx]: opt }));
  };

  return (
    <div data-testid="methodologie-view" className="space-y-10">
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#15294F] via-[#1E3A6F] to-[#15294F] text-white rounded-2xl shadow-keyto p-7 md:p-9">
        <p className="text-[10px] font-bold uppercase tracking-widest text-amber-300 mb-2">
          Logique de saisie
        </p>
        <h1 className="font-heading text-3xl md:text-4xl font-extrabold leading-tight">
          Le sens des variations
        </h1>
        <p className="mt-3 text-sm md:text-base text-white/70 max-w-3xl">
          Pour chaque type de compte, ce tableau indique de quel côté on
          enregistre une <strong className="text-amber-300">hausse</strong>{" "}
          (« plus ») et de quel côté on enregistre une{" "}
          <strong className="text-amber-300">baisse</strong> (« moins »). C'est la
          base de toute écriture comptable.
        </p>
      </div>

      {/* Tableau récapitulatif */}
      <div className="bg-white border border-keyto rounded-2xl shadow-keyto overflow-x-auto">
       <div className="min-w-[640px] md:min-w-0">
        {/* En-tête */}
        <div className="grid grid-cols-[140px_1fr] md:grid-cols-[180px_1fr] bg-slate-100 border-b border-keyto">
          <div className="p-3 text-center border-r border-keyto">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
              Types de comptes
            </p>
          </div>
          <div className="p-3 text-center">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
              Sens des variations
            </p>
          </div>
        </div>

        {VARIATIONS_ROWS.map((row, ri) => (
          <div
            key={ri}
            className={`grid grid-cols-[180px_1fr] ${
              ri < VARIATIONS_ROWS.length - 1 ? "border-b border-keyto" : ""
            }`}
          >
            <div className="flex items-center justify-center p-4 bg-slate-50 border-r border-keyto text-center">
              <p className="font-heading text-base font-extrabold text-[#15294F] leading-tight">
                {row.type}
              </p>
            </div>
            <div
              className={`grid gap-px bg-slate-200 ${
                row.cells.length === 3
                  ? "grid-cols-1 md:grid-cols-3"
                  : "grid-cols-1 md:grid-cols-2"
              }`}
            >
              {row.cells.map((cell, ci) => {
                const growsLeft = cell.growSide === "left";
                return (
                  <div key={ci} className="bg-white p-5">
                    <p className="text-center font-bold text-sm text-[#15294F] border-b-2 border-slate-300 pb-2 mb-4">
                      {cell.label}
                    </p>
                    <div className="relative grid grid-cols-2 min-h-[100px]">
                      <div className="absolute top-0 bottom-0 left-1/2 w-px bg-slate-300 -translate-x-px" />
                      <div className="flex flex-col items-center justify-center gap-1 p-2">
                        {growsLeft ? (
                          <>
                            <ArrowUpRight size={28} className="text-[#2F7A5C]" strokeWidth={2.5} />
                            <span className="text-2xl font-extrabold text-[#2F7A5C] leading-none">+</span>
                            <span className="text-xs font-bold text-[#2F7A5C] uppercase tracking-wider">plus</span>
                          </>
                        ) : (
                          <>
                            <ArrowDownLeft size={28} className="text-[#A33B4B]" strokeWidth={2.5} />
                            <span className="text-2xl font-extrabold text-[#A33B4B] leading-none">−</span>
                            <span className="text-xs font-bold text-[#A33B4B] uppercase tracking-wider">moins</span>
                          </>
                        )}
                      </div>
                      <div className="flex flex-col items-center justify-center gap-1 p-2">
                        {growsLeft ? (
                          <>
                            <ArrowDownLeft size={28} className="text-[#A33B4B] scale-x-[-1]" strokeWidth={2.5} />
                            <span className="text-2xl font-extrabold text-[#A33B4B] leading-none">−</span>
                            <span className="text-xs font-bold text-[#A33B4B] uppercase tracking-wider">moins</span>
                          </>
                        ) : (
                          <>
                            <ArrowUpRight size={28} className="text-[#2F7A5C]" strokeWidth={2.5} />
                            <span className="text-2xl font-extrabold text-[#2F7A5C] leading-none">+</span>
                            <span className="text-xs font-bold text-[#2F7A5C] uppercase tracking-wider">plus</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 mt-2 text-center text-[10px] font-bold uppercase tracking-wider">
                      <span className="text-slate-500">Débit</span>
                      <span className="text-slate-500">Crédit</span>
                    </div>
                    {cell.examples && (
                      <p className="mt-3 text-center text-[11px] text-slate-600 font-mono">
                        ex : {cell.examples}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
       </div>
      </div>

      {/* Vérifiez-vous : tous les quiz */}
      <div>
        <p className="text-[10px] font-bold uppercase tracking-widest text-[#A56A2E] mb-2">
          Vérifiez-vous
        </p>
        <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-[#15294F] mb-6">
          {QUIZZES.length} questions pour valider votre compréhension
        </h2>

        <div className="space-y-5">
          {QUIZZES.map((quiz, idx) => {
            const picked = answers[idx];
            const answered = picked !== undefined;
            return (
              <div
                key={idx}
                data-testid={`quiz-${idx}`}
                className="bg-white border border-keyto rounded-2xl shadow-keyto p-6 md:p-7"
              >
                <div className="flex items-start gap-3 mb-4">
                  <span className="shrink-0 w-7 h-7 rounded-full bg-[#15294F] text-white text-xs font-bold flex items-center justify-center">
                    {idx + 1}
                  </span>
                  <h3 className="font-heading text-base md:text-lg font-extrabold text-[#15294F] leading-tight pt-1">
                    {quiz.q}
                  </h3>
                </div>
                <div className="space-y-2 ml-10">
                  {quiz.options.map((opt, oi) => {
                    const isCorrect = oi === quiz.good;
                    const isPicked = picked === oi;
                    return (
                      <button
                        key={oi}
                        data-testid={`quiz-${idx}-option-${oi}`}
                        disabled={answered}
                        onClick={() => pick(idx, oi)}
                        className={`w-full text-left p-3 rounded-lg border-2 transition flex items-center gap-3 ${
                          !answered
                            ? "border-keyto hover:border-[#2D6BE0] hover:bg-blue-50/30 cursor-pointer"
                            : isCorrect
                            ? "border-emerald-500 bg-emerald-50"
                            : isPicked
                            ? "border-rose-500 bg-rose-50"
                            : "border-keyto opacity-60"
                        }`}
                      >
                        <span
                          className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                            !answered
                              ? "bg-slate-100 text-slate-500"
                              : isCorrect
                              ? "bg-emerald-500 text-white"
                              : isPicked
                              ? "bg-rose-500 text-white"
                              : "bg-slate-100 text-slate-400"
                          }`}
                        >
                          {!answered
                            ? String.fromCharCode(65 + oi)
                            : isCorrect ? (
                              <Check size={12} />
                            ) : isPicked ? (
                              <X size={12} />
                            ) : (
                              String.fromCharCode(65 + oi)
                            )}
                        </span>
                        <span className="text-sm font-semibold text-[#15294F]">
                          {opt}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {answered && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-4 ml-10 flex items-start gap-3 p-4 bg-[#15294F] text-white rounded-lg"
                  >
                    <Lightbulb size={18} className="shrink-0 mt-0.5 text-amber-300" />
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-amber-300 mb-1">
                        {picked === quiz.good ? "Bonne réponse" : "Pas tout à fait"}
                      </p>
                      <p className="text-sm leading-relaxed">{quiz.explain}</p>
                    </div>
                  </motion.div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
